// btw-front/src/components/admin/dashboard/StaleAlertSettingCard.jsx
// [관리 대상 딜] 자동 알림 상태·빠른 토글 + 접이식 상세 설정(임계 일수·발송 시각·즉시 발송)
// props 는 StaleDealsTab 의 상태/핸들러 이름을 그대로 받는다(부모 식별자와 1:1).
import React from "react";
import { useTranslation } from "react-i18next";
import { Switch } from "antd";
import { pad2 } from "./shared";

// 자동 발송 시각 선택지 — 시(0~23) / 분(15분 단위)
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = [0, 15, 30, 45];

export default function StaleAlertSettingCard({
  days,
  form,
  loading,
  quickToggleEnabled,
  saveSetting,
  saving,
  sendNow,
  sending,
  setForm,
  setSettingsOpen,
  setting,
  settingsOpen,
  toggling,
}) {
  const { t } = useTranslation();
  return (
    <>
      {/* 자동 알림 상태 + 빠른 토글 (항상 표시). 상세 설정은 접이식 */}
      <section className="rounded-2xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
          <div className="flex items-center gap-2.5">
            <Switch
              size="small"
              checked={!!(setting && setting.isEnabled)}
              loading={toggling}
              disabled={!setting}
              onChange={quickToggleEnabled}
            />
            <span className="text-sm font-medium text-slate-800">
              {t("admin.staleDeals.setting.autoAlert")}
            </span>
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                setting && setting.isEnabled
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-slate-100 text-slate-500"
              }`}
            >
              {setting && setting.isEnabled
                ? t("admin.staleDeals.setting.statusOn")
                : t("admin.staleDeals.setting.statusOff")}
            </span>
          </div>

          <div className="ml-auto flex items-center gap-3">
            {setting && (
              <span className="hidden text-xs text-slate-400 sm:inline">
                {t("admin.staleDeals.setting.activeSummary", {
                  days: setting.thresholdDays,
                  time: setting.sendAt,
                })}
              </span>
            )}
            <button
              type="button"
              onClick={() => setSettingsOpen((o) => !o)}
              className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
            >
              {t("admin.staleDeals.setting.details")}
              <span className="text-[10px] text-slate-400">{settingsOpen ? "▲" : "▼"}</span>
            </button>
          </div>
        </div>

        {settingsOpen && (
          <div className="border-t border-slate-200 px-4 py-4">
            <p className="text-xs text-slate-500">{t("admin.staleDeals.setting.desc")}</p>
            <p className="mt-1 text-xs text-slate-400">{t("admin.staleDeals.setting.saveHint")}</p>
            <div className="mt-4 flex flex-wrap items-end gap-x-4 gap-y-3">
              <label className="flex flex-col gap-1 text-xs text-slate-500">
                {t("admin.staleDeals.setting.thresholdDays")}
                <select
                  value={form.thresholdDays}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, thresholdDays: Number(e.target.value) }))
                  }
                  className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-800"
                >
                  {[3, 5, 7, 14, 21, 30, 60, 90].map((d) => (
                    <option key={d} value={d}>
                      {t("admin.staleDeals.setting.daysOption", { days: d })}
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex flex-col gap-1 text-xs text-slate-500">
                {t("admin.staleDeals.setting.sendAt")}
                <div className="flex items-center gap-1">
                  <select
                    value={form.hour}
                    onChange={(e) => setForm((f) => ({ ...f, hour: Number(e.target.value) }))}
                    className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-800"
                  >
                    {HOURS.map((h) => (
                      <option key={h} value={h}>
                        {pad2(h)}
                      </option>
                    ))}
                  </select>
                  <span className="text-sm text-slate-500">:</span>
                  <select
                    value={form.minute}
                    onChange={(e) => setForm((f) => ({ ...f, minute: Number(e.target.value) }))}
                    className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-800"
                  >
                    {MINUTES.map((m) => (
                      <option key={m} value={m}>
                        {pad2(m)}
                      </option>
                    ))}
                  </select>
                </div>
              </label>

              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={saveSetting}
                  disabled={saving}
                  className="rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-white transition hover:opacity-90 disabled:opacity-50"
                >
                  {t("admin.staleDeals.setting.save")}
                </button>
                <button
                  type="button"
                  onClick={sendNow}
                  disabled={sending}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
                >
                  {t("admin.staleDeals.setting.sendNow")}
                </button>
              </div>
            </div>
            <p className="mt-3 text-xs text-slate-400">
              {t("admin.staleDeals.setting.lastRun")}:{" "}
              {setting && setting.lastRunOn
                ? `${setting.lastRunOn} (${t("admin.staleDeals.setting.lastRunCount", {
                    n: setting.lastRunDealCount ?? 0,
                  })})`
                : t("admin.staleDeals.setting.never")}
            </p>
          </div>
        )}
      </section>
    </>
  );
}
